/**
 * Centralized image library for Electrocase.fr
 *
 * Each entry has:
 *   - `slug`: file basename (matches /public/img/{slug}-{sm|md|lg}.{webp|jpg})
 *   - `alt`: SEO-friendly default alt text (French, descriptive, contextual)
 *   - `credit`: Photographer attribution (optional, Unsplash license = free use)
 *   - `ratio`: aspect ratio for layout (e.g. "4/3", "16/9")
 *
 * Source: Unsplash (license commerciale & non-commerciale, gratuite, sans attribution requise).
 */

export const IMAGES = {
    "hero-laundry": {
        alt: "Buanderie moderne avec lave-linge — exemple d'intervention Electrocase à domicile",
        ratio: "4/3",
    },
    "hero-laundry-pair": {
        alt: "Lave-linge et sèche-linge installés dans une buanderie moderne",
        ratio: "16/9",
    },
    "washing-machine": {
        alt: "Lave-linge à hublot blanc en vue frontale",
        ratio: "4/3",
    },
    "fridge-modern": {
        alt: "Réfrigérateur 3 portes en inox dans une cuisine moderne",
        ratio: "4/3",
    },
    "oven-knobs": {
        alt: "Boutons de commande d'un four électrique encastrable",
        ratio: "4/3",
    },
    "technician-tools": {
        alt: "Outils de réparation d'électroménager : perceuse et accessoires",
        ratio: "4/3",
    },
    "fridge-repair": {
        alt: "Technicien Electrocase réparant un réfrigérateur inox à domicile à Paris",
        ratio: "4/3",
    },
    "dishwasher-repair": {
        alt: "Technicien Electrocase inspectant le bras de lavage d'un lave-vaisselle encastré",
        ratio: "4/3",
    },
    "oven-repair": {
        alt: "Technicien Electrocase remplaçant la résistance d'un four encastrable",
        ratio: "4/3",
    },
    "brand-bosch": { alt: "Lave-linge Bosch dans une buanderie parisienne — réparation à domicile", ratio: "4/3" },
    "brand-siemens": { alt: "Four encastrable Siemens iQ à écran tactile dans une cuisine moderne", ratio: "4/3" },
    "brand-samsung": { alt: "Réfrigérateur connecté 4 portes Samsung dans une cuisine familiale parisienne", ratio: "4/3" },
    "brand-lg": { alt: "Colonne lave-linge sèche-linge LG dans un appartement parisien", ratio: "4/3" },
    "brand-whirlpool": { alt: "Cuisine familiale avec lave-linge et lave-vaisselle Whirlpool", ratio: "4/3" },
    "brand-miele": { alt: "Cuisine haut de gamme avec électroménager Miele encastré", ratio: "4/3" },
    "brand-electrolux": { alt: "Cuisine scandinave avec lave-vaisselle Electrolux ouvert", ratio: "4/3" },
    "brand-beko": { alt: "Cuisine lumineuse avec réfrigérateur et lave-linge Beko", ratio: "4/3" },
    "brand-aeg": { alt: "Buanderie premium avec lave-linge et sèche-linge AEG", ratio: "4/3" },
    "brand-candy": { alt: "Lave-linge compact Candy dans un studio parisien", ratio: "4/3" },
    "brand-liebherr": { alt: "Réfrigérateur et cave à vin Liebherr dans un appartement haussmannien", ratio: "4/3" },
    "brand-de-dietrich": { alt: "Four pyrolyse De Dietrich noir mat dans une cuisine de luxe parisienne", ratio: "4/3" },
    "kitchen-clean": {
        alt: "Cuisine équipée moderne avec électroménager intégré",
        ratio: "4/3",
    },
    "soldering": {
        alt: "Réparation électronique d'une carte de puissance d'électroménager",
        ratio: "4/3",
    },
    "stove-knobs": {
        alt: "Plaque de cuisson avec boutons de commande",
        ratio: "4/3",
    },
    "repair-shop": {
        alt: "Atelier de réparation d'électroménager avec pièces détachées",
        ratio: "4/3",
    },
    "paris-skyline": {
        alt: "Vue panoramique de Paris avec ses immeubles haussmanniens",
        ratio: "16/9",
    },
    "paris-rooftops": {
        alt: "Toits de Paris vue depuis un immeuble parisien",
        ratio: "16/9",
    },
};

/**
 * Map each appliance to its 3 images: hero, close-up, technician/repair.
 * Used by ServicePage, CityServicePage, PannePage, PanneGeoPage.
 */
export const APPLIANCE_IMAGES = {
    "lave-linge": [
        { slug: "washing-machine", alt: "Lave-linge à hublot — réparation à domicile" },
        { slug: "technician-tools", alt: "Outils du réparateur lave-linge en intervention" },
        { slug: "hero-laundry-pair", alt: "Lave-linge et sèche-linge dans une buanderie" },
    ],
    "lave-vaisselle": [
        { slug: "kitchen-clean", alt: "Lave-vaisselle intégré dans une cuisine moderne" },
        { slug: "dishwasher-repair", alt: "Diagnostic d'un lave-vaisselle encastré par un technicien Electrocase" },
        { slug: "soldering", alt: "Réparation carte électronique de lave-vaisselle" },
    ],
    "frigo": [
        { slug: "fridge-modern", alt: "Réfrigérateur 3 portes inox — diagnostic à domicile" },
        { slug: "fridge-repair", alt: "Technicien Electrocase réparant un réfrigérateur à domicile à Paris" },
        { slug: "kitchen-clean", alt: "Frigo encastré dans une cuisine équipée" },
    ],
    "four": [
        { slug: "oven-knobs", alt: "Boutons de commande d'un four électrique" },
        { slug: "oven-repair", alt: "Technicien Electrocase remplaçant la résistance d'un four encastrable" },
        { slug: "stove-knobs", alt: "Plaque de cuisson et four — diagnostic" },
    ],
    "seche-linge": [
        { slug: "hero-laundry-pair", alt: "Sèche-linge et lave-linge — réparation à domicile" },
        { slug: "repair-shop", alt: "Atelier de réparation sèche-linge" },
        { slug: "soldering", alt: "Carte électronique de sèche-linge" },
    ],
};

/** Pictures for the "Pourquoi nous choisir" / trust sections (E-E-A-T). */
export const TRUST_IMAGES = [
    { slug: "repair-shop", alt: "Atelier Electrocase avec stock pièces détachées" },
    { slug: "technician-tools", alt: "Outils du technicien Electrocase en intervention" },
    { slug: "paris-rooftops", alt: "Intervention Electrocase sur les toits de Paris" },
];
